import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { THEME } from '@/lib/theme';
import { useT } from '@/lib/i18n/useT';
import type { TouchpointResponseAnalytics } from '@/lib/api/types';

interface ResponsesOverTimeChartProps {
  data: TouchpointResponseAnalytics;
  height?: number;
}

function formatBucket(bucket: string, granularity: 'hour' | 'day'): string {
  if (granularity === 'hour') {
    // "2026-05-17T14:00" → "May 17 14:00"
    const d = new Date(bucket);
    if (Number.isNaN(d.getTime())) return bucket;
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  // "2026-05-17" → "May 17"
  const d = new Date(bucket + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return bucket;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function ResponsesOverTimeChart({
  data,
  height = 240,
}: ResponsesOverTimeChartProps) {
  const t = useT();
  const series = data.timeSeries.map((b) => ({
    label: formatBucket(b.bucket, data.bucket),
    count: b.count,
  }));

  if (series.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-[var(--color-fg-muted)]">
        {t('stations.detail.dashboardEmpty')}
      </p>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={series} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 11, fill: 'var(--color-fg-muted)' }}
          tickLine={false}
        />
        <YAxis
          allowDecimals={false}
          tick={{ fontSize: 11, fill: 'var(--color-fg-muted)' }}
          tickLine={false}
          axisLine={false}
        />
        <Tooltip
          cursor={{ fill: 'var(--color-bg-elevated)' }}
          contentStyle={{
            background: 'var(--color-bg-surface)',
            border: '1px solid var(--color-border-default)',
            borderRadius: 8,
            fontSize: 12,
          }}
        />
        <Bar dataKey="count" fill={THEME.accent} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
